import React, { useState, useEffect } from 'react';
import { collectionsAPI } from '../services/api';
import './AgentPages.css';

const CollectionHistory = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    fetchCollections();
  }, [page, fromDate, toDate]);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate })
      };

      const response = await collectionsAPI.getAgentHistory(params);

      // Handle paginated response format
      if (response && response.data) {
        setCollections(response.data);
        setTotal(response.pagination?.total || response.total || response.data.length);
      } else if (Array.isArray(response)) {
        setCollections(response);
        setTotal(response.length);
      } else if (response && response.collections) {
        setCollections(response.collections);
        setTotal(response.total || response.collections.length);
      } else {
        setCollections([]);
        setTotal(0);
      }
    } catch (err) {
      setError('Failed to load collection history: ' + (err.message || 'Network error'));
      setCollections([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    setPage(1);
    fetchCollections();
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  // Calculate summary statistics
  const totalCollectionAmount = collections.reduce((sum, col) => sum + (Number(col.emi_amount) || 0), 0);
  const todayCollections = collections.filter(col => {
    const today = new Date().toISOString().split('T')[0];
    const colDate = new Date(col.collection_date).toISOString().split('T')[0];
    return colDate === today;
  });
  const todayAmount = todayCollections.reduce((sum, col) => sum + (Number(col.emi_amount) || 0), 0);

  if (loading && collections.length === 0) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Collection History</h1>
          <p>View all your collection records</p>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loader"></div>
          <p>Loading collection history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Collection History</h1>
        <p>View all collections recorded by you across all retailers and loans</p>
      </div>

      {/* Statistics Cards */}
      {collections.length > 0 && (
        <div className="grid grid-3" style={{ marginBottom: '30px' }}>
          <div className="dashboard-card">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <div className="card-label">Total Collections</div>
              <div className="card-value">{collections.length}</div>
              <div className="card-subtitle">in selected period</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <div className="card-label">Total Amount</div>
              <div className="card-value">₹{totalCollectionAmount.toLocaleString()}</div>
              <div className="card-subtitle">collected so far</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">📅</div>
            <div className="card-content">
              <div className="card-label">Today's Collections</div>
              <div className="card-value">₹{todayAmount.toLocaleString()}</div>
              <div className="card-subtitle">{todayCollections.length} collections</div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="form-container" style={{ marginBottom: '20px' }}>
        <div className="form-row">
          <div className="form-group">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ paddingTop: '25px' }}>
            <button
              className="button button-primary"
              onClick={handleFilterChange}
              disabled={loading}
            >
              {loading ? 'Filtering...' : 'Filter'}
            </button>
            <button
              className="button button-secondary"
              onClick={handleClearFilters}
              style={{ marginLeft: '10px' }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Collections Table */}
      {collections.length > 0 ? (
        <div>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Collection Date</th>
                  <th>Agent/Retailer</th>
                  <th>Loan #</th>
                  <th>Amount (₹)</th>
                  <th>Payment Mode</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {collections.map((col, idx) => (
                  <tr key={idx}>
                    <td>{new Date(col.collection_date).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div>{col.retailer_name || 'N/A'}</div>
                      <small style={{ color: '#666' }}>{col.retailer_code || '#N/A'} • {col.phone || 'N/A'}</small>
                    </td>
                    <td><strong>{col.loan_code || '#' + (col.loan_id || 'N/A')}</strong></td>
                    <td>
                      <strong>₹{Number(col.emi_amount || 0).toLocaleString()}</strong>
                    </td>
                    <td>{col.payment_method ? col.payment_method.toUpperCase() : col.payment_mode ? col.payment_mode.toUpperCase() : 'N/A'}</td>
                    <td>
                      <span className={`status-badge status-${col.status?.toLowerCase() || 'pending'}`}>
                        {col.status || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
              <button
                className="button button-secondary"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loading}
              >
                Previous
              </button>
              <span style={{ alignSelf: 'center', padding: '0 15px' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="button button-secondary"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || loading}
              >
                Next
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h2>No Collections Found</h2>
          <p>
            {fromDate || toDate
              ? 'No collections found for the selected date range.'
              : 'You haven\'t recorded any collections yet. Start recording collections to see them here.'}
          </p>
          {fromDate || toDate ? (
            <button className="button button-primary" onClick={handleClearFilters}>
              Clear Filters
            </button>
          ) : (
            <a href="/record-collection" className="button button-primary">
              Record Collection
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default CollectionHistory;
